import { Link } from '@tanstack/react-router';
import { Play, ArrowRight, BookOpen } from 'lucide-react';

export interface ContinueTarget {
  topicId: string;
  topicTitle: string;
  moduleTitle: string;
  pct: number;
}

/** Hub prime-slot card shown when there is no live session: deep-links to the resume lesson. */
export function BatchContinueCard({ batchId, target }: { batchId: string; target: ContinueTarget }) {
  return (
    <div
      className="relative flex flex-col gap-6 overflow-hidden rounded-[24px] p-6 sm:min-h-[250px] sm:flex-row sm:items-center sm:gap-8 sm:p-8"
      style={{ background: 'linear-gradient(167.74deg, #f5f3ff 0%, #faf5ff 45%, #fce7f3 100%)' }}
    >
      <div aria-hidden="true" className="pointer-events-none absolute left-[-40px] top-[130px] size-40 rounded-full opacity-[0.15]"
        style={{ background: 'radial-gradient(circle, #8b5cf6 0%, transparent 70%)' }} />

      <div className="flex items-center gap-2 sm:absolute sm:left-8 sm:top-6">
        <span className="size-2 rounded-full bg-[#8b5cf6]" />
        <span className="text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-[#8b5cf6]">Your Next Action</span>
      </div>

      <div className="flex size-20 shrink-0 items-center justify-center rounded-[20px] bg-[#8b5cf6]/10 sm:mt-4 sm:size-[120px]">
        <BookOpen className="size-9 text-[#8b5cf6] sm:size-[52px]" />
      </div>

      <div className="flex min-w-0 flex-1 flex-col sm:pt-4">
        <p className="text-xs font-semibold uppercase leading-4 tracking-[0.3px] text-[#9ca3af]">Pick up where you left off</p>
        <h2 className="pt-1 text-[28px] font-bold leading-tight tracking-tight text-[#111827] sm:text-[42px] sm:leading-[52.5px]">
          {target.topicTitle}
        </h2>
        <div className="flex items-center gap-3 pt-4 text-sm text-[#6b7280]">
          <span>{target.moduleTitle}</span>
          <span className="size-1 rounded-full bg-[#d1d5db]" />
          <span>{target.pct}% complete</span>
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-3 sm:w-[199px] sm:pt-4">
        <Link
          to="/courses/$batchId/topics/$topicId"
          params={{ batchId, topicId: target.topicId }}
          search={{ tab: 'overview' }}
          className="flex h-14 items-center justify-center gap-2 rounded-[14px] px-7 text-sm font-semibold text-white shadow-[0px_10px_7.5px_rgba(139,92,246,0.18),0px_4px_3px_rgba(139,92,246,0.12)] transition-opacity hover:opacity-90"
          style={{ background: 'linear-gradient(164.26deg, #8b5cf6 0%, #7c3aed 100%)' }}
        >
          <Play className="size-4 fill-white text-white" />
          Continue Learning
        </Link>
        <Link
          to="/courses/$batchId/learn"
          params={{ batchId }}
          className="flex h-14 items-center justify-center gap-1.5 rounded-[14px] border border-[#ececec] bg-white px-7 text-sm font-semibold text-[#111827] transition-colors hover:bg-[#f9fafb]"
        >
          Browse Curriculum
          <ArrowRight className="size-4" />
        </Link>
      </div>
    </div>
  );
}
